import type {UndoableState} from '../../vendor/src/features/remotion-editor/editor/state/types';
import type {AudioItem} from '../../vendor/src/features/remotion-editor/editor/items/audio/audio-item-type';

export type NarrationCutResult =
  | {ok:true;state:UndoableState;replacements:Record<string,string[]>}
  | {ok:false;state:UndoableState;reason:string;itemId?:string};

/** Non-destructive source windows, not audio generation. The caller must explicitly identify narration. */
export function splitNarrationAtMainCuts(state:UndoableState, narrationItemIds:readonly string[], mainTrackId?:string):NarrationCutResult {
  const fail=(reason:string,itemId?:string):NarrationCutResult=>({ok:false,state,reason,itemId});
  if(!Number.isFinite(state.fps)||state.fps<=0)return fail('invalid-fps');
  const candidates=state.tracks.filter(track=>mainTrackId?track.id===mainTrackId:
    track.metadata?.isMainTrack===true&&track.metadata.type==='video');
  if(candidates.length!==1)return fail('main-track-required');
  const main=candidates[0]!;
  const boundaries=new Set<number>();
  for(const id of main.items){
    const item=state.items[id];
    if(!item)return fail('missing-main-item',id);
    if(!Number.isSafeInteger(item.from)||item.from<0||!Number.isSafeInteger(item.durationInFrames)||item.durationInFrames<=0
      ||!Number.isSafeInteger(item.from+item.durationInFrames))return fail('invalid-main-window',id);
    if(item.type==='group')return fail('grouped-main-requires-layout-review',id);
    boundaries.add(item.from);boundaries.add(item.from+item.durationInFrames);
  }
  const cuts=[...boundaries].sort((a,b)=>a-b);
  const plans:Array<{item:AudioItem;starts:number[]}> = [];
  for(const id of new Set(narrationItemIds)){
    const item=state.items[id];
    if(!item||item.type!=='audio')return fail('narration-item-required',id);
    if(state.assets[item.assetId]?.type!=='audio')return fail('narration-asset-required',id);
    if(!Number.isSafeInteger(item.from)||item.from<0||!Number.isSafeInteger(item.durationInFrames)||item.durationInFrames<=0||!Number.isSafeInteger(item.from+item.durationInFrames)
      ||!Number.isFinite(item.audioStartFromInSeconds)||item.audioStartFromInSeconds<0)return fail('invalid-narration-window',id);
    const track=state.tracks.find(track=>track.items.includes(id));
    if(!track)return fail('narration-track-required',id);
    if(track.locked)return fail('locked-narration',id);
    if(Object.values(state.items).some(parent=>parent.type==='group'&&parent.children.includes(id)))return fail('grouped-narration-requires-layout-review',id);
    if(!Number.isFinite(item.playbackRate)||item.playbackRate<=0)return fail('invalid-playback-rate',id);
    const inside=cuts.filter(frame=>frame>item.from&&frame<item.from+item.durationInFrames);
    if(!inside.length)continue;
    // Do not silently change an existing fade curve or the company's specialized TTS playback path.
    if(item.audioFadeInDurationInSeconds!==0||item.audioFadeOutDurationInSeconds!==0||item.renderMeta?.vendorModel){
      return fail('audio-envelope-requires-review',id);
    }
    const starts=[item.from,...inside];
    for(const start of inside){
      if(!Number.isFinite(item.audioStartFromInSeconds+((start-item.from)/state.fps)*item.playbackRate))return fail('invalid-narration-window',id);
      const derivedId=`${id}:voice-cut:${start}`;
      if(derivedId.length>512||state.items[derivedId])return fail('item-id-conflict',id);
    }
    plans.push({item,starts});
  }
  if(!plans.length)return{ok:true,state,replacements:{}};
  const items={...state.items};
  const replacements:Record<string,string[]>={};
  for(const {item,starts} of plans){
    const end=item.from+item.durationInFrames;
    replacements[item.id]=starts.map((from,index)=>{
      const id=index===0?item.id:`${item.id}:voice-cut:${from}`;
      items[id]={...item,id,from,durationInFrames:(starts[index+1]??end)-from,
        audioStartFromInSeconds:item.audioStartFromInSeconds+((from-item.from)/state.fps)*item.playbackRate};
      return id;
    });
  }
  const tracks=state.tracks.map(track=>({...track,items:track.items.flatMap(id=>replacements[id]??[id])}));
  return{ok:true,state:{...state,items,tracks},replacements};
}
